import uniqBy from "lodash/uniqBy";
import { useLocation } from "react-router-dom";

import {
  useBookmarkStatus,
  useDeleteVoteOnStatus,
  useFavouriteStatus,
  useReblogStatus,
  useUnBookmarkStatus,
  useUnfavouriteStatus,
  useUnreblogStatus,
  useVoteOnStatus,
} from "@/api/statuses";
import { useAnalytics } from "@/contexts/analytics-context";
import { useAuth } from "@/contexts/auth/useAuth";
import { useComposer } from "@/contexts/composer-context";
import { useModalContext } from "@/contexts/modal-context";
import { useTimelineContext } from "@/contexts/timeline-context";
import { useEngagementCapture } from "@/hooks/useEngagementCapture";
import {
  Mention,
  mentionSchema,
  PredictionStatus,
  type Status,
} from "@/schemas";
import { getStatusFromCache } from "@/utils/hotkeys";
import { getStatusMentions } from "@/utils/status";

type StatusActionProps = {
  status?: Status | PredictionStatus;
  isHotkey?: boolean;
  initialStatusId?: string;
};

function getMentionsForStatus(status: Status): Mention[] {
  const accountMention = mentionSchema.parse(status.account);
  const mentions = getStatusMentions(status);

  if (!mentions.includes(accountMention)) {
    mentions.unshift(accountMention);
  }

  return uniqBy(mentions, "id");
}

function isPredictionStatus(
  status: Status | PredictionStatus,
): status is PredictionStatus {
  return "prediction" in status;
}

function isRegularStatus(status: Status | PredictionStatus): status is Status {
  return "account" in status;
}

function useStatusActions({
  status: propStatus,
  isHotkey = false,
  initialStatusId,
}: StatusActionProps) {
  const { currentAccount } = useAuth();
  const { openModal } = useModalContext();
  const { track } = useAnalytics();
  const { pathname } = useLocation();
  const { timelineId } = useTimelineContext();
  const { captureEngagement, snapshotEngagementContext } =
    useEngagementCapture();

  const bookmarkStatus = useBookmarkStatus();
  const deleteVoteOnStatus = useDeleteVoteOnStatus();
  const favouriteStatus = useFavouriteStatus();
  const reblogStatus = useReblogStatus();
  const unbookmarkStatus = useUnBookmarkStatus();
  const unfavouriteStatus = useUnfavouriteStatus();
  const unreblogStatus = useUnreblogStatus();
  const voteOnStatus = useVoteOnStatus();

  const composeId = propStatus?.id || "status";
  const { updateComposer } = useComposer(composeId);

  const isLoading =
    bookmarkStatus.isLoading ||
    deleteVoteOnStatus.isLoading ||
    favouriteStatus.isLoading ||
    reblogStatus.isLoading ||
    unbookmarkStatus.isLoading ||
    unfavouriteStatus.isLoading ||
    unreblogStatus.isLoading ||
    voteOnStatus.isLoading;

  const getStatus = (): Status | PredictionStatus | undefined => {
    // If actions is called as hotkey, status is not provided as prop,
    // so we need to get the status from cache.
    if (isHotkey) {
      return getStatusFromCache(initialStatusId);
    }

    return propStatus;
  };

  function trackEvent(name: string, status?: Status | PredictionStatus) {
    const properties: { feed_type?: string; video_type?: string } = {};

    if (pathname === "/") {
      properties.feed_type = timelineId;
    }

    if (status && isPredictionStatus(status)) {
      track(name, properties);
      return;
    }

    if (status?.tv) {
      properties.video_type =
        new Date(status.tv.end_time) < new Date() ? "Replay" : "Livestream";
    }

    track(name, properties);
  }

  function handleReblog() {
    const status = getStatus();
    if (!status) return;

    if (!currentAccount) {
      openModal("UNAUTHORIZED", {
        source: "retruth",
      });
      return;
    }

    if (status.reblogged) {
      unreblogStatus.mutate({ id: status.id });
    } else {
      reblogStatus.mutate({ id: status.id });

      if (isRegularStatus(status)) captureEngagement("retruth", status);
      trackEvent("RTTruth", status);
    }
  }

  function handleFavourite() {
    const status = getStatus();
    if (!status || !isRegularStatus(status)) return;

    if (!currentAccount) {
      openModal("UNAUTHORIZED", {
        source: "favorite",
      });
      return;
    }

    if (status.favourited) {
      unfavouriteStatus.mutate({ id: status.id });
    } else {
      favouriteStatus.mutate({ id: status.id });
      captureEngagement("favourite", status);
      trackEvent("LikedTruth", status);
    }
  }

  function handleBookmark() {
    const status = getStatus();
    if (!status || !isRegularStatus(status)) return;

    if (!currentAccount) {
      openModal("UNAUTHORIZED", {
        source: "bookmark",
      });
      return;
    }

    if (!currentAccount.bookmarks_onboarded) {
      openModal("BOOKMARKS_ONBOARDING", {
        onClose: () => {
          bookmarkStatus.mutate({ statusId: status.id });
          captureEngagement("bookmark", status);
        },
      });
      return;
    }

    if (status.bookmarked) {
      unbookmarkStatus.mutate({ statusId: status.id });
    } else {
      bookmarkStatus.mutate({ statusId: status.id });
      captureEngagement("bookmark", status);
    }
  }

  function handleUpvote() {
    const status = getStatus();
    if (!status || !isRegularStatus(status)) return;

    if (!currentAccount) {
      openModal("UNAUTHORIZED", {
        source: "vote",
      });
      return;
    }

    if (status.reaction === "upvote") {
      return deleteVoteOnStatus.mutate(status.id);
    }

    return voteOnStatus.mutate({
      reaction: "upvote",
      status: status as Status,
    });
  }

  function handleDownvote() {
    const status = getStatus();
    if (!status || !isRegularStatus(status)) return;

    if (!currentAccount) {
      openModal("UNAUTHORIZED", {
        source: "vote",
      });
      return;
    }

    if (status.reaction === "downvote") {
      return deleteVoteOnStatus.mutate(status.id);
    }

    return voteOnStatus.mutate({
      reaction: "downvote",
      status: status as Status,
    });
  }

  function handleReply() {
    const status = getStatus();
    if (!status) return;

    if (currentAccount) {
      const mentions = !isPredictionStatus(status)
        ? getMentionsForStatus(status)
        : [];

      updateComposer({
        inReplyTo: mentions,
        inReplyToStatusId: status.id,
        groupId: status.group?.id,
        engagementContext: isRegularStatus(status)
          ? snapshotEngagementContext(status)
          : undefined,
      });

      openModal("COMPOSE", { composeId, groupId: status.group?.id });
    } else {
      openModal("UNAUTHORIZED", { source: "reply" });
    }
  }

  const handleQuote: React.EventHandler<React.MouseEvent> = () => {
    const status = getStatus();
    if (!status) return;

    if (currentAccount) {
      updateComposer({
        embed: status.prediction
          ? { type: "prediction", statusId: status.id }
          : { type: "status", statusId: status.id },
        engagementContext: isRegularStatus(status)
          ? snapshotEngagementContext(status)
          : undefined,
      });

      openModal("COMPOSE", { composeId });
    } else {
      openModal("UNAUTHORIZED", { source: "retruth" });
    }
  };

  const handleShare = () => {
    const status = getStatus();
    if (!status) return;

    const text = isPredictionStatus(status)
      ? status.prediction?.title
      : status.search_index;
    if (isRegularStatus(status)) captureEngagement("share", status);

    navigator
      .share({
        text,
        url: status.url,
      })
      .catch((e) => {
        if (e.name !== "AbortError") console.error(e);
      });
  };

  return {
    handleBookmark,
    handleDownvote,
    handleFavourite,
    handleReblog,
    handleUpvote,
    handleReply,
    handleQuote,
    handleShare,
    isLoading,
  };
}

export { useStatusActions };
